import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMachineSearch } from '../api/queries.ts';
import { Input, Notice, PageTitle } from '../components/ui.tsx';

type Detector = { detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>> };
type DetectorCtor = new (options: { formats: string[] }) => Detector;

/**
 * `/scan` – QR-Code in der App scannen (§10.1). Nutzt den eingebauten
 * BarcodeDetector des Browsers (Chrome/Edge auf Android, Safari ab 17). Ohne
 * ihn: Kamera-App des Handys verwenden oder Inventarnummer eintippen.
 */
export function ScanPage() {
  const navigate = useNavigate();
  const video = useRef<HTMLVideoElement>(null);
  const [supported] = useState(() => 'BarcodeDetector' in window);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!supported) return;
    let stream: MediaStream | null = null;
    let stopped = false;
    const Ctor = (window as unknown as { BarcodeDetector: DetectorCtor }).BarcodeDetector;
    const detector = new Ctor({ formats: ['qr_code'] });
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        while (!stopped) {
          const codes = await detector.detect(video.current).catch(() => []);
          const raw = codes[0]?.rawValue;
          const match = raw ? /\/m\/([0-9A-HJKMNP-TV-Z]{6})/i.exec(raw) : null;
          if (match?.[1]) {
            stopped = true;
            void navigate({ to: '/m/$code', params: { code: match[1].toUpperCase() } });
            break;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch {
        setError('Die Kamera ist nicht verfügbar. Bitte die Kamera-App des Handys verwenden oder die Inventarnummer eingeben.');
      }
    })();
    return () => {
      stopped = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [supported, navigate]);

  const search = useMachineSearch(q.trim(), { pageSize: 8 });

  return (
    <div className="mx-auto max-w-[640px] space-y-5">
      <PageTitle lead="Den QR-Code auf dem Etikett der Maschine ins Bild halten.">QR-Code scannen</PageTitle>
      {supported && !error ? (
        <video ref={video} className="aspect-square w-full bg-steel-900 object-cover" muted playsInline />
      ) : (
        <Notice tone="warn">{error ?? 'Dieser Browser kann QR-Codes nicht selbst lesen. Bitte die Kamera-App des Handys verwenden. Sie öffnet die Maschinenseite direkt.'}</Notice>
      )}
      <Input label="Oder Inventarnummer eingeben" value={q} onChange={setQ} inputMode="search" placeholder="z. B. FRD-L-001" />
      {search.note ? <Notice tone="warn">{search.note}</Notice> : null}
      {search.items.map((m) => (
        <button
          key={m.id}
          type="button"
          className="ad-card ad-card-clickable flex w-full items-center gap-3 text-left"
          onClick={() => void navigate({ to: '/melden', search: { machineId: m.id } })}
        >
          <span className="font-mono text-sm">{m.inventoryNo}</span>
          <span className="font-semibold">{m.name}</span>
          <span className="ml-auto text-sm text-[var(--text-muted)]">{m.areaName}</span>
        </button>
      ))}
    </div>
  );
}
